import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Mail, MapPin, ShieldCheck, CreditCard, Truck, Sparkles, Award, Users, Star, Globe } from "lucide-react";
import ContactForm from "../components/ContactForm";

export default function AboutPage() {
  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>About Us | Carepoint Health Supplies</title>
        <meta name="description" content="Carepoint Health Supplies — UK supplier of medical, care home, dental, GP surgery, pharmacy, and school supplies since 2014. Trusted by NHS trusts and care homes nationwide." />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#005EB8] to-[#00a99d] text-white py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <span className="inline-block bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
            About Carepoint
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Helping the UK's healthcare professionals deliver better care.</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Carepoint Health Supplies has been a registered UK company since 2014. From our distribution
            hub we now ship to NHS trusts, GP surgeries, care homes, dentists, pharmacies and schools
            right across the country.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12 space-y-12">

        {/* Stats strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: <Users className="w-6 h-6 mx-auto mb-2 text-[#00a99d]" />, value: "60+", label: "Team Members" },
            { icon: <Sparkles className="w-6 h-6 mx-auto mb-2 text-[#00a99d]" />, value: "24,000+", label: "Products in Stock" },
            { icon: <Award className="w-6 h-6 mx-auto mb-2 text-[#00a99d]" />, value: "10+", label: "Years Trading" },
            { icon: <Star className="w-6 h-6 mx-auto mb-2 text-[#00a99d]" />, value: "100,000+", label: "5-Star Reviews" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl p-5 text-center shadow-sm border">
              {s.icon}
              <div className="font-bold text-2xl text-gray-900">{s.value}</div>
              <div className="text-sm text-gray-500">{s.label}</div>
            </div>
          ))}
        </section>

        {/* Our story */}
        <section className="bg-white rounded-xl shadow-sm border p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Story</h2>
          <div className="prose prose-gray max-w-none text-gray-700 leading-relaxed space-y-3">
            <p>
              Carepoint Health Supplies has been a registered UK company since <strong>2014</strong>.
              From our distribution centre we have grown to a team of over <strong>60 staff</strong> and
              run a logistics operation covering the whole of mainland UK with next-working-day delivery.
            </p>
            <p>
              We are specialists in medical, dental, pharmacy and care home consumables. Our long-standing
              relationships with manufacturers and industry professionals enable us to supply the high
              quality products you use most, at fantastically competitive prices.
            </p>
          </div>
        </section>

        {/* Why us */}
        <section className="bg-white rounded-xl shadow-sm border p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Dealing with Carepoint is simple, safe and secure</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="flex gap-3">
              <Phone className="w-6 h-6 text-[#00a99d] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Real, friendly people on the phone</h3>
                <p className="text-sm text-gray-600">Our UK-based sales team is here Monday to Friday 9 am – 5 pm to help with quotes, orders and any product question. Reach us by phone, email or live chat.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <ShieldCheck className="w-6 h-6 text-[#00a99d] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">SSL secured & GDPR compliant</h3>
                <p className="text-sm text-gray-600">Every page on this site uses a verified SSL certificate. Your data is encrypted and never shared.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <CreditCard className="w-6 h-6 text-[#00a99d] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Multiple secure payment options</h3>
                <p className="text-sm text-gray-600">Pay securely with Stripe, SumUp, BACS or 30-day Instant Credit (subject to approval) — all backed by Strong Customer Authentication.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Award className="w-6 h-6 text-[#00a99d] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">30-day no-quibble guarantee</h3>
                <p className="text-sm text-gray-600">Not happy with your order? Email orders@carepointuk.net or call 020 3633 8047 to arrange collection and a full refund — no questions asked.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">100,000+ verified 5-star reviews</h2>
          <p className="text-gray-700 max-w-2xl mx-auto mb-5">
            Over the years we have built long-lasting relationships with our customers. They have kindly
            written thousands of positive, independent and verified reviews across Google and Trustpilot —
            so you can rest reassured you are in good hands.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center gap-2 bg-[#005EB8] text-white px-4 py-2 rounded-full text-sm font-semibold">
              <Star className="w-4 h-4 fill-current" /> 4.8 / 5 on Google Reviews
            </span>
            <span className="inline-flex items-center gap-2 bg-[#00b67a] text-white px-4 py-2 rounded-full text-sm font-semibold">
              <Star className="w-4 h-4 fill-current" /> Excellent on Trustpilot
            </span>
          </div>
        </section>

        {/* Customer guarantees */}
        <section className="bg-white rounded-xl shadow-sm border p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">Our Customer Guarantees</h2>
          <ul className="space-y-4">
            <li className="border-l-4 border-[#00a99d] pl-4">
              <strong className="text-gray-900">£500 Instant Credit</strong>
              <p className="text-sm text-gray-700 mt-1">
                We are pleased to offer Instant Credit facilities to established UK organisations,
                allowing orders by purchase order paid within 30 days of invoice. No formal application
                required — simply choose this option at checkout. We carry out basic verification checks
                and reserve the right to approve or decline credit at our discretion.
              </p>
            </li>
            <li className="border-l-4 border-[#00a99d] pl-4">
              <strong className="text-gray-900">30-Day No-Quibble Guarantee</strong>
              <p className="text-sm text-gray-700 mt-1">
                Email <a className="text-[#00a99d] underline" href="mailto:orders@carepointuk.net">orders@carepointuk.net</a>
                {" "}or call <a className="text-[#00a99d] underline" href="tel:02036338047">020 3633 8047</a> to
                arrange collection and a full refund.
              </p>
            </li>
            <li className="border-l-4 border-[#00a99d] pl-4">
              <strong className="text-gray-900">Free Delivery on orders over £50</strong>
              <p className="text-sm text-gray-700 mt-1">Orders over £50 (before VAT and carriage) automatically qualify for free delivery to mainland UK addresses.</p>
            </li>
            <li className="border-l-4 border-[#00a99d] pl-4">
              <strong className="text-gray-900">Price Promise</strong>
              <p className="text-sm text-gray-700 mt-1">Seen a better price on a like-for-like product? Call us or email <a className="text-[#00a99d] underline" href="mailto:orders@carepointuk.net">orders@carepointuk.net</a> and we'll do our best to match — or beat — it.</p>
            </li>
          </ul>
        </section>

        {/* Accreditation */}
        <section className="bg-white rounded-xl shadow-sm border p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Accreditation & Quality Standards</h2>
          <p className="text-gray-700">
            At Carepoint Health Supplies we respect human rights and do not tolerate any form of modern
            slavery. We comply fully with the <strong>Modern Slavery Act 2015</strong>, ISO standards
            relevant to medical product distribution, and all UK Medicines and Healthcare products
            Regulatory Agency (MHRA) guidance applicable to our product range.
          </p>
        </section>

        {/* Contact card */}
        <section className="bg-gradient-to-br from-[#005EB8] to-[#00a99d] text-white rounded-xl shadow-lg p-8" data-testid="contact-card">
          <h2 className="text-2xl font-bold mb-5">Contact Carepoint Health Supplies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="mb-4 text-white/90">
                Whether you need infection control items, diagnostic sundries, gloves or even tissues, we
                are sure you'll find the ideal product within our range. Operating hours: Monday – Friday
                9 am – 5 pm (closed bank holidays).
              </p>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                <div>
                  <strong>Carepoint Health Supplies</strong><br />
                  128 City Road<br />
                  London, Greater London<br />
                  EC1V 2NX<br />
                  United Kingdom
                </div>
              </div>
            </div>
            <div className="space-y-3 text-white/95">
              <a href="tel:02036338047" className="flex items-center gap-3 hover:text-white transition" data-testid="contact-phone">
                <Phone className="w-5 h-5" />
                <span><strong>Phone:</strong> 020 3633 8047</span>
              </a>
              <a href="mailto:orders@carepointuk.net" className="flex items-center gap-3 hover:text-white transition" data-testid="contact-email">
                <Mail className="w-5 h-5" />
                <span><strong>Email:</strong> orders@carepointuk.net</span>
              </a>
              <a href="https://carepointuk.net/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white transition" data-testid="contact-website">
                <Globe className="w-5 h-5" />
                <span><strong>Website:</strong> carepointuk.net</span>
              </a>
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5" />
                <span>Free delivery on orders over £50 (excl. VAT)</span>
              </div>
              <div className="pt-2">
                <Link
                  to="/products"
                  className="inline-block bg-white text-[#00a99d] font-semibold px-5 py-2 rounded-lg hover:bg-yellow-300 hover:text-[#003544] transition"
                  data-testid="about-cta-shop"
                >
                  Browse our products &rarr;
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="bg-white rounded-xl shadow-sm border p-8" data-testid="contact-form-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Send us a message</h2>
          <p className="text-gray-600 mb-6">
            Have a question about a product, need a bulk quote, or want to set up a trade account?
            Fill in the form below and our team will reply within 1 working day.
          </p>
          <ContactForm />
        </section>
      </div>
    </div>
  );
}
